import { Button, ButtonBase, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import {
  EmphasizedNumber,
  IconButton,
  InformationModal,
  PointDisplay,
} from 'utoniq-ui'

import {
  Maybe,
  PaymentMethod,
  PrepaidPointsPaymentSourceFragment,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'
import { isCarrierPaymentMethod } from '@/lib/escott'

import { BottomSheet } from '../../bottomSheet'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '../../functionals/PaymentErrorModal'
import { PaymentMethodSelectDrawerContent } from '../PaymentMethodSelectDrawer'

export type PointChargeDrawerProps = {
  /** ドロワーの開閉状態 */
  open: boolean
  /** 現在のポイント残高 */
  point: number
  /**ドロワーを閉じるときに呼ばれるコールバック */
  onClose: () => void
  /** ポイント残高やプラン取得中のローディング状態 */
  loading: boolean
  /** チャージ処理中のローディング状態 */
  chargeLoading: boolean
  /**
   * 購入ボタン押下時に呼ばれるコールバック
   * @param plan 選択されたポイントプラン
   * @param paymentMethod 選択された決済手段
   */
  onPurchase: (
    plan: PurchasablePrepaidPointDetailFragment,
    paymentMethod: Maybe<PaymentMethod>
  ) => Promise<void>
  /** 購入可能なポイントプラン一覧 */
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  /** クレジットカード情報 */
  paymentSource: Maybe<PrepaidPointsPaymentSourceFragment>
  /** ページ名（トラッキング用など）*/
  pageName?: string
  /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
  enablePointCarrierPayment: boolean
}

/**
 * ポイントを購入できるボトムシート
 */
export const PointChargeDrawer: Organism<PointChargeDrawerProps> = ({
  open,
  point,
  onClose,
  loading,
  chargeLoading,
  onPurchase,
  purchasablePrepaidPoints,
  paymentSource,
  pageName,
  enablePointCarrierPayment,
}) => {
  const { t } = useTranslation('prepaidPointCharge')
  const { router } = useRouter<{ status?: string; points?: string }>()
  const isWeb = getPlatform() === 'web'
  const { showMessage, showError } = useMessage()
  const { analytics } = useAnalytics()
  // ドロワー内部の画面（購入プラン選択・決済手段選択など）を切り替えるための state
  const [view, setView] = useState<'plan' | 'payment'>('plan')
  const [currentPlan, setCurrentPlan] =
    useState<PurchasablePrepaidPointDetailFragment | null>(null)
  // 決済手段選択にて選択された決済手段を保持する state
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [openPurchaseComplete, setOpenPurchaseComplete] =
    useState<boolean>(false)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  const handlePurchase = useCallback(
    async (selectedPaymentMethod: Maybe<PaymentMethod>) => {
      if (!currentPlan) return

      onPurchase(currentPlan, selectedPaymentMethod)
        .then(() => {
          // キャリア決済の場合は外部キャリア決済画面にて
          // 追加手続きが必要なため完了モーダルを表示しない
          if (isCarrierPaymentMethod(selectedPaymentMethod)) return

          setView('plan')
          setOpenConfirmModal(false)
          onClose()
          setOpenPurchaseComplete(true)
        })
        .catch(e => {
          switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
            /**
             * @description
             * 3Dセキュア認証が必要な場合
             */
            case constants.errorCodes.REQUIRE_3D_SECURE:
              setOpenConfirmModal(false)
              onClose()
              setOpenRequire3DSecureModal(true)
              break
            /**
             * @desciption
             * カードが有効期限切れの場合
             */
            case constants.errorCodes.EXPIRED_CARD:
              setOpenConfirmModal(false)
              onClose()
              setOpenExpiredCardModal(true)
              break
            /**
             * @description
             * 何かしらの決済エラーの場合
             */
            case constants.errorCodes.PAYMENT_ERROR:
              setOpenConfirmModal(false)
              onClose()
              setOpenPaymentErrorModal(true)
              break
            default:
              showError(e)
              break
          }
        })
    },
    [currentPlan, onPurchase, onClose, showError]
  )

  // 外部キャリア決済画面からのリダイレクトで戻ってきた際
  // トーストメッセージを表示する処理
  useEffect(() => {
    // キャリア決済画面からのリダイレクトで戻ってきたとき以外は何もしない
    // リダイレクト時にはクエリパラメータに status が含まれる
    if (!router.query.status) return

    // ローディング中もしくはポイント残高が取得できていない場合は何もしない
    if (loading || point === null) return

    switch (router.query.status) {
      case 'success':
        setOpenPurchaseComplete(true)
        break
      case 'cancel':
        showMessage(t('carrierCancel'))
        break
      case 'error':
        showError(t('carrierError'))
        break
      // ここには到達しない想定
      default:
        break
    }
  }, [loading])

  return (
    <>
      <BottomSheet
        open={open}
        onClose={() => {
          onClose()
          setView('plan')
        }}
        breakpoints={[0, 0.6, 1]}
        initialBreakpoint={0.6}
      >
        <BaseLayout
          header={{
            title: t('title'),
            left: view === 'payment' && (
              <IconButton.Prev
                edge="start"
                size="large"
                onClick={() => setView('plan')}
              />
            ),
            right: (
              <IconButton.Close
                edge="end"
                size="small"
                onClick={() => {
                  onClose()
                  setView('plan')
                }}
              />
            ),
            sx: { pt: 0 },
          }}
          safeArea={{ pt: 0 }}
        >
          {view === 'plan' && (
            <Stack sx={{ px: 5 }}>
              <PointDisplayCell point={point} />
              <Stack sx={{ py: 4 }} spacing={4}>
                {purchasablePrepaidPoints?.edges.map(({ node }) => (
                  <PointChargeButton
                    key={node.id}
                    prepaidPoint={node}
                    onClick={() => {
                      analytics?.track('click_point', {
                        productId: node.metadata.id,
                        prepaidPointId: node.prepaidPointId,
                        pageName,
                      })
                      setCurrentPlan(node)
                      if (isWeb) {
                        setView('payment')
                      } else {
                        // ネイティブの場合ポイントを含むデジタルアイテムの決済は
                        // IAP を経由して実行するため
                        // プラン選択後すぐに確認モーダルを表示する
                        setOpenConfirmModal(true)
                      }
                    }}
                  />
                ))}
                <TextLinkCell />
              </Stack>
            </Stack>
          )}
          {view === 'payment' && (
            <PaymentMethodSelectDrawerContent
              paymentSource={paymentSource}
              chargeLoading={chargeLoading}
              enablePointCarrierPayment={enablePointCarrierPayment}
              onNavigate={async () => {
                await onClose()
                setView('plan')
              }}
              onSubmit={paymentMethod => {
                setSelectedPaymentMethod(paymentMethod)
                onClose()
                setView('plan')
                setOpenConfirmModal(true)
              }}
            />
          )}
        </BaseLayout>
      </BottomSheet>

      {/* 購入確認モーダル */}
      <InformationModal
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        title={t('confirmCharge')}
        content={t('confirmChargeDescription', {
          point: currentPlan?.points,
          price: currentPlan?.price,
        })}
        buttons={
          <>
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                handlePurchase(selectedPaymentMethod)
              }}
              loading={chargeLoading}
            >
              {t('toBuy')}
            </Button>
            <Button
              sx={{ minWidth: 212 }}
              variant="text"
              color="primary"
              onClick={() => setOpenConfirmModal(false)}
              disabled={chargeLoading}
            >
              {t('cancel')}
            </Button>
          </>
        }
      />

      {/* 購入完了モーダル */}
      <InformationModal
        open={openPurchaseComplete}
        onClose={() => setOpenPurchaseComplete(false)}
        title={t('completeCharge')}
        content={t('completeChargeDescription', {
          // キャリア決済画面からのリダイレクトで戻ってきた場合は
          // クエリパラメータから points を読み込み表示する
          point: currentPlan?.points || router.query.points,
        })}
        buttons={
          <Button
            sx={{ minWidth: 212, pointerEvents: 'auto' }}
            variant="contained"
            color="primary"
            size="large"
            onClick={event => {
              event.stopPropagation()
              event.preventDefault()
              setOpenPurchaseComplete(false)
            }}
          >
            {t('ok')}
          </Button>
        }
      />

      <ExpiredCardModal
        open={openExpiredCardModal}
        onClose={() => setOpenExpiredCardModal(false)}
      />

      <PaymentErrorModal
        open={openPaymentErrorModal}
        onClose={() => setOpenPaymentErrorModal(false)}
      />

      <Require3DSecureModal
        open={openRequire3DSecureModal}
        onClose={() => setOpenRequire3DSecureModal(false)}
      />
    </>
  )
}

type PointDisplayCellProps = {
  point: number
}
const PointDisplayCell: Cell<PointDisplayCellProps> = ({ point }) => {
  const { t } = useTranslation('prepaidPointCharge')
  return (
    <CellFrame sx={{ pt: 2, pb: 3 }}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="h6" color="text.secondary">
          {t('pointBalance')}
        </Typography>
        <PointDisplay size="large" point={point} />
      </Stack>
    </CellFrame>
  )
}

type PointChargeButtonProps = {
  prepaidPoint: PurchasablePrepaidPointDetailFragment
  onClick: () => void
}
const PointChargeButton: Cell<PointChargeButtonProps> = ({
  onClick,
  prepaidPoint,
}) => {
  return (
    <ButtonBase
      sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 5 }}
      onClick={onClick}
    >
      <Stack flex={1} direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2}>
          <PointDisplay point={prepaidPoint.points} />
          <EmphasizedNumber variant="overline" color="primary.main">
            {prepaidPoint.description}
          </EmphasizedNumber>
        </Stack>
        <EmphasizedNumber variant="h5">¥{prepaidPoint.price}</EmphasizedNumber>
      </Stack>
    </ButtonBase>
  )
}

type TextLinkCellProps = {}
const TextLinkCell: Cell<TextLinkCellProps> = () => {
  const { t } = useTranslation('prepaidPointCharge')
  return (
    <CellFrame sx={{ pb: 2 }}>
      <Stack alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('cancelPurchaseNote')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}
